import {
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  ExternalLink,
  FileText,
  Plus,
} from 'lucide-react'
import { useState } from 'react'
import { FaFacebook } from 'react-icons/fa'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  facebookAdsLeadgenFormManagement,
  type FacebookAdsLeadgenFormManagementResult,
} from 'sprites-types/workflow/tools/facebookAdsLeadgenFormManagement'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import { SchemaValidationError } from '../shared/safeParseToolResult'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'

function FacebookAdsLeadgenFormManagementDisplayComponent({
  nodeExecution,
}: WorkflowToolComponentProps) {
  const [isQuestionsExpanded, setIsQuestionsExpanded] = useState(true)
  const schema = getToolResultSchema(facebookAdsLeadgenFormManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as FacebookAdsLeadgenFormManagementResult

  const isCreate = 'created' in result && result.created

  const name = 'name' in result ? result.name : undefined

  // Extract metadata from inputs
  const inputs = nodeExecution.inputs.functionArgs as any
  const params = inputs?.action?.params || {}
  const adAccountId = params.adAccountId
  const pageId = params.pageId
  const questions = params.questions || []
  const privacyPolicyUrl = params.privacyPolicyUrl
  const contextCard = params.contextCard
  const thankYouPage = params.thankYouPage

  // Build Facebook Business Dashboard URL for leadgen form
  // Facebook may not support deep linking to specific forms, but we'll try common patterns
  // If none work, the link will at least take users to the forms page where they can search
  const businessDashboardUrl = `https://business.facebook.com/latest/instant_forms/forms`

  // Context menu actions
  const contextActions: ContextAction[] = [
    {
      id: 'view-in-business-manager',
      label: 'View in Business Manager',
      icon: <ExternalLink className="h-4 w-4" />,
      onClick: () => window.open(businessDashboardUrl, '_blank', 'noopener,noreferrer'),
    },
    {
      id: 'copy-form-id',
      label: 'Copy Form ID',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        try {
          await navigator.clipboard.writeText(result.id)
        } catch (err) {
          console.error('Failed to copy form ID:', err)
        }
      },
    },
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-start gap-4 px-4 py-3">
          <div className="mt-1 flex-shrink-0">
            <div className="rounded-full bg-blue-100 p-2">
              {isCreate ? (
                <Plus className="h-5 w-5 text-blue-600" />
              ) : (
                <FileText className="h-5 w-5 text-blue-600" />
              )}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            {/* Header */}
            <div className="mb-3 flex items-center gap-2">
              <FaFacebook className="h-4 w-4 text-[#0064e0]" />
              <div className="text-sm font-semibold text-gray-900">
                {isCreate ? 'Instant Form Created' : 'Instant Form'}
              </div>
              {isCreate && (
                <div className="ml-auto flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-700">
                  <CheckCircle className="h-3 w-3" />
                  <span>Ready to use</span>
                </div>
              )}
            </div>

            {/* Main Content Card */}
            <div className="space-y-4">
              {/* Form Details Section */}
              <div className="rounded-xl border border-gray-200 bg-white p-3">
                {name && (
                  <div className="mb-3 border-b border-gray-100 pb-2">
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Form Name
                    </div>
                    <div className="mt-1 text-sm font-semibold text-gray-900">{name}</div>
                  </div>
                )}

                <div className="space-y-2.5">
                  {/* Form ID - Prominent */}
                  <div>
                    <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                      Form ID
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(result.id)
                        } catch (err) {
                          console.error('Failed to copy form ID:', err)
                        }
                      }}
                      className="group flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 transition-colors hover:border-blue-300 hover:bg-blue-50"
                      title="Click to copy Form ID"
                    >
                      <span className="font-mono text-xs font-medium text-gray-700 group-hover:text-blue-700">
                        {result.id}
                      </span>
                      <Copy className="h-3.5 w-3.5 text-gray-400 group-hover:text-blue-600" />
                    </button>
                  </div>

                  {/* Account Info */}
                  {(adAccountId || pageId) && (
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {adAccountId && (
                        <div>
                          <div className="text-gray-500">Ad Account</div>
                          <div className="mt-0.5 font-medium text-gray-700">{adAccountId}</div>
                        </div>
                      )}
                      {pageId && (
                        <div>
                          <div className="text-gray-500">Page ID</div>
                          <div className="mt-0.5 font-medium text-gray-700">{pageId}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Questions Section */}
              {questions.length > 0 && (
                <div className="rounded-xl border border-gray-200 bg-white p-3">
                  <button
                    onClick={() => setIsQuestionsExpanded(!isQuestionsExpanded)}
                    className="mb-2 flex w-full items-center justify-between transition-colors hover:opacity-80"
                  >
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Questions
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                        {questions.length} {questions.length === 1 ? 'question' : 'questions'}
                      </div>
                      {isQuestionsExpanded ? (
                        <ChevronUp className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </button>
                  {isQuestionsExpanded && (
                    <div className="space-y-2">
                      {questions.map((q: any, index: number) => {
                        const displayLabel =
                          q.type === 'CUSTOM'
                            ? q.label || 'Custom Question'
                            : q.type
                                .replace(/_/g, ' ')
                                .toLowerCase()
                                .replace(/\b\w/g, (c: string) => c.toUpperCase())
                        return (
                          <div
                            key={index}
                            className="rounded-lg border border-gray-100 bg-gray-50 p-2.5"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{displayLabel}</div>
                                <div className="mt-0.5 text-xs text-gray-500">{q.type}</div>
                              </div>
                              {q.options && q.options.length > 0 && (
                                <div className="ml-2 rounded bg-white px-2 py-1 text-xs text-gray-600">
                                  {q.options.length} {q.options.length === 1 ? 'option' : 'options'}
                                </div>
                              )}
                            </div>
                            {q.options && q.options.length > 0 && (
                              <div className="mt-2 rounded bg-white px-2 py-1.5">
                                <div className="text-xs font-medium text-gray-500">Options:</div>
                                <div className="mt-1 flex flex-wrap gap-1.5">
                                  {q.options.map(
                                    (opt: { key: string; value: string }, optIdx: number) => (
                                      <span
                                        key={optIdx}
                                        className="rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
                                      >
                                        {opt.value}
                                      </span>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Settings Sections */}
              {(privacyPolicyUrl || contextCard || thankYouPage) && (
                <div className="space-y-3">
                  {privacyPolicyUrl && (
                    <div className="rounded-xl border border-gray-200 bg-white p-3">
                      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                        Privacy Policy
                      </div>
                      <a
                        href={privacyPolicyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="break-all text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {privacyPolicyUrl}
                      </a>
                    </div>
                  )}

                  {contextCard &&
                    (contextCard.title ||
                      contextCard.body ||
                      contextCard.buttonText ||
                      contextCard.coverPhotoId) && (
                      <div className="rounded-xl border border-gray-200 bg-white p-3">
                        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                          Context Card
                        </div>
                        <div className="space-y-1.5 text-xs">
                          {contextCard.title && (
                            <div>
                              <span className="font-medium text-gray-600">Title:</span>{' '}
                              <span className="text-gray-900">{contextCard.title}</span>
                            </div>
                          )}
                          {contextCard.body && (
                            <div>
                              <span className="font-medium text-gray-600">Body:</span>{' '}
                              <span className="text-gray-900">{contextCard.body}</span>
                            </div>
                          )}
                          {contextCard.buttonText && (
                            <div>
                              <span className="font-medium text-gray-600">Button:</span>{' '}
                              <span className="text-gray-900">{contextCard.buttonText}</span>
                            </div>
                          )}
                          {contextCard.coverPhotoId && (
                            <div className="pt-1">
                              <span className="text-xs text-gray-500">
                                Cover Photo ID: {contextCard.coverPhotoId}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                  {thankYouPage && (
                    <div className="rounded-xl border border-gray-200 bg-white p-3">
                      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                        Thank You Page
                      </div>
                      <div className="space-y-1.5 text-xs">
                        {thankYouPage.title && (
                          <div>
                            <span className="font-medium text-gray-600">Title:</span>{' '}
                            <span className="text-gray-900">{thankYouPage.title}</span>
                          </div>
                        )}
                        {thankYouPage.body && (
                          <div>
                            <span className="font-medium text-gray-600">Body:</span>{' '}
                            <span className="text-gray-900">{thankYouPage.body}</span>
                          </div>
                        )}
                        <div>
                          <span className="font-medium text-gray-600">Button Type:</span>{' '}
                          <span className="text-gray-900">{thankYouPage.buttonType}</span>
                        </div>
                        {thankYouPage.buttonText && (
                          <div>
                            <span className="font-medium text-gray-600">Button Text:</span>{' '}
                            <span className="text-gray-900">{thankYouPage.buttonText}</span>
                          </div>
                        )}
                        {thankYouPage.buttonUrl && (
                          <div>
                            <span className="font-medium text-gray-600">Button URL:</span>{' '}
                            <a
                              href={thankYouPage.buttonUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="break-all font-medium text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {thankYouPage.buttonUrl}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="rounded-xl border border-blue-100 bg-blue-50 p-3">
                <a
                  href={businessDashboardUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-medium text-blue-700 hover:text-blue-900"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  <span>View in Business Manager</span>
                </a>
                <div className="mt-1.5 text-[10px] text-blue-600">
                  Tip: Use the search on the forms page to find this form by ID
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ContextMenuWrapper>
  )
}

function downloadFacebookAdsLeadgenFormManagement(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function facebookAdsLeadgenFormManagementToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(facebookAdsLeadgenFormManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data as FacebookAdsLeadgenFormManagementResult
  const isCreate = 'created' in result && result.created

  const name = 'name' in result ? result.name : undefined

  // Extract metadata from inputs
  const inputs = nodeExecution.inputs.functionArgs as any
  const params = inputs?.action?.params || {}
  const adAccountId = params.adAccountId
  const pageId = params.pageId
  const questions = params.questions || []
  const privacyPolicyUrl = params.privacyPolicyUrl
  const contextCard = params.contextCard
  const thankYouPage = params.thankYouPage

  let markdown = `## Facebook Instant Form ${isCreate ? 'Created' : ''}\n\n`
  if (name) markdown += `**Name:** ${name}\n`
  markdown += `**Form ID:** ${result.id}\n`
  if (adAccountId) markdown += `**Ad Account:** ${adAccountId}\n`
  if (pageId) markdown += `**Page ID:** ${pageId}\n`
  if (questions.length > 0) {
    markdown += `\n**Questions (${questions.length}):**\n\n`
    questions.forEach((q: any, index: number) => {
      const displayLabel =
        q.type === 'CUSTOM'
          ? q.label || 'Custom Question'
          : q.type
              .replace(/_/g, ' ')
              .toLowerCase()
              .replace(/\b\w/g, (c: string) => c.toUpperCase())
      markdown += `${index + 1}. **${displayLabel}** (${q.type})\n`
      if (q.options && q.options.length > 0) {
        markdown += `   Options: ${q.options.map((opt: { key: string; value: string }) => opt.value).join(', ')}\n`
      }
      markdown += '\n'
    })
  }
  if (privacyPolicyUrl) {
    markdown += `\n**Privacy Policy:** ${privacyPolicyUrl}\n`
  }

  if (
    contextCard &&
    (contextCard.title || contextCard.body || contextCard.buttonText || contextCard.coverPhotoId)
  ) {
    markdown += `\n**Context Card:**\n`
    if (contextCard.title) markdown += `- Title: ${contextCard.title}\n`
    if (contextCard.body) markdown += `- Body: ${contextCard.body}\n`
    if (contextCard.buttonText) markdown += `- Button Text: ${contextCard.buttonText}\n`
    if (contextCard.coverPhotoId) markdown += `- Cover Photo ID: ${contextCard.coverPhotoId}\n`
  }

  if (thankYouPage) {
    markdown += `\n**Thank You Page:**\n`
    if (thankYouPage.title) markdown += `- Title: ${thankYouPage.title}\n`
    if (thankYouPage.body) markdown += `- Body: ${thankYouPage.body}\n`
    markdown += `- Button Type: ${thankYouPage.buttonType}\n`
    if (thankYouPage.buttonText) markdown += `- Button Text: ${thankYouPage.buttonText}\n`
    if (thankYouPage.buttonUrl) markdown += `- Button URL: ${thankYouPage.buttonUrl}\n`
  }

  markdown += '\n---\n'

  return markdown
}

export const FacebookAdsLeadgenFormManagementDisplay = createWorkflowToolComponent(
  FacebookAdsLeadgenFormManagementDisplayComponent,
  downloadFacebookAdsLeadgenFormManagement,
  facebookAdsLeadgenFormManagementToMarkdown
)
